import { nextTick } from "vue";

import { EventBus } from "@/shared/projects/event-bus";
import { copy } from "@/shared/utils/string";
import { cloneDeep, randomId } from "@/shared/utils/helpers.js";
import { iframeMutations } from "@/shared/store/iframe.mutations";
import { faqSnapshotService } from "@/apps/faq/store/faq-snapshot.service";
import { t } from "@/i18n";
import { projectsService } from "@/shared/projects/projects.service";
import {
    PlayerIcon,
    BookIcon,
    BusinessHandshakeIcon,
    FaqIcon,
    LayoutListIcon,
    LayoutBoardIcon,
    LayoutTableIcon,
    LayoutColumnsIcon,
    LayoutTtitleAsideIcon,
    LayoutSliderIcon,
} from "@easy/ui/icons";
import PreviewList from "@/apps/faq/project/onboarding/svg-preview/PreviewList.vue";
import PreviewBoard from "@/apps/faq/project/onboarding/svg-preview/PreviewBoard.vue";
import PreviewTable from "@/apps/faq/project/onboarding/svg-preview/PreviewTable.vue";
import PreviewColumns from "@/apps/faq/project/onboarding/svg-preview/PreviewColumns.vue";
import PreviewAside from "@/apps/faq/project/onboarding/svg-preview/PreviewAside.vue";
import PreviewSlider from "@/apps/faq/project/onboarding/svg-preview/PreviewSlider.vue";

export default {
    namespaced: true,
    state: () => ({
        faq: {},
        sections: [],
        socialsGhost: {},
        iframe: null,
        iframeLoaded: false,
        autoSaveMode: true,
        hasChanges: false,
        hasSnapshot: false,
    }),
    getters: {
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.faq,
        nQuestions: (state) =>
            state.sections.reduce(
                (total, section) => total + section.questions.filter((q) => !q._deleted).length,
                0
            ),
        templates: () => [
            {
                value: t("faq.templates.empty"),
                id: "empty",
            },
            {
                value: t("faq.templates.video"),
                icon: PlayerIcon,
                id: "video",
            },
            {
                value: "E-book",
                icon: BookIcon,
                id: "ebook",
            },
            {
                value: t("faq.templates.consulting"),
                icon: BusinessHandshakeIcon,
                id: "consulting",
            },
            {
                value: "Easyfaq",
                icon: FaqIcon,
                id: "faqs",
            },
        ],
        layouts: () => [
            {
                value: "list",
                label: t("faq.layout.list"),
                icon: LayoutListIcon,
                preview: PreviewList,
            },
            {
                value: "board",
                label: t("faq.layout.board"),
                icon: LayoutBoardIcon,
                preview: PreviewBoard,
            },
            {
                value: "table",
                label: t("faq.layout.table"),
                icon: LayoutTableIcon,
                preview: PreviewTable,
            },
            {
                value: "columns",
                label: t("faq.layout.columns"),
                icon: LayoutColumnsIcon,
                preview: PreviewColumns,
            },
            {
                value: "aside",
                label: t("faq.layout.aside"),
                icon: LayoutTtitleAsideIcon,
                preview: PreviewAside,
            },
            {
                value: "slider",
                label: t("faq.layout.slider"),
                info: t("faq.layout.slider_info"),
                icon: LayoutSliderIcon,
                preview: PreviewSlider,
            },
        ],
        faqIframe: (state, g, rootState) => {
            return `<iframe
                id="${state.faq.uuid}"
                width="100%"
                src="${rootState.globalUrl}/faqs/${state.faq.uuid}"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                frameborder="0"
                scrolling="no"
                ></iframe>
                <script> window.addEventListener("message", (e) => {!!e.data.frameHeight && e.data.faqUuid === "${state.faq.uuid}" && (document.getElementById("${state.faq.uuid}").style.height = e.data.frameHeight);});</script>`;
        },
        sectionsToShow: (state) => {
            return state.sections.filter((sec) => !sec._deleted);
        },
    },
    mutations: {
        ...iframeMutations,
        setProject(state, { faq, sections, snapshot = {} }) {
            state.faq = faqSnapshotService.makeSnapshotState(state, "faq", faq, snapshot);
            state.sections = faqSnapshotService.makeSnapshotState(
                state,
                "sections",
                sections,
                snapshot
            );
            state.faq.socials = faqSnapshotService.makeSnapshotState(
                state,
                "socialsGhost",
                state.faq.socials || {},
                snapshot
            );
            state.hasChanges = false;
        },
        clearProject(state) {
            state.faq = {};
            state.sections = [];
            faqSnapshotService.clearSnapshot(state, "faq", {});
            faqSnapshotService.clearSnapshot(state, "sections", []);
            faqSnapshotService.clearSnapshot(state, "socialsGhost", {});
        },
        changeQuestion(state, payload) {
            const updatedQuestion = Object.assign({}, payload);

            const section = state.sections.find((x) => x.id === payload.sectionId);
            const index = section.questions.map((x) => x.id).indexOf(payload.id);
            if (index > -1) {
                section.questions.splice(index, 1, updatedQuestion);
            }
        },
        changeSection(state, payload) {
            const updatedSection = Object.assign({}, payload);

            const index = state.sections.map((x) => x.id).indexOf(payload.id);
            if (index > -1) {
                state.sections.splice(index, 1, updatedSection);
            }
        },
        setQuestions(state) {
            state.sections.forEach((section) => {
                section.questions
                    .filter((q) => !q._deleted)
                    .forEach((question, index) => {
                        question.sectionId = section.id;
                        question.order = index;
                        if (!question._created && !question._deleted) {
                            question["_changed"] = true;
                        }
                    });
            });
        },
        removeQuestion(state, question) {
            if (question._created) {
                const section = state.sections.find((x) => x.id === question.sectionId);
                const index = section.questions.map((x) => x.id).indexOf(question.id);
                if (index > -1) {
                    section.questions.splice(index, 1);
                }
            } else {
                if (question._changed) {
                    delete question._changed;
                }
                question["_deleted"] = true;
            }
        },
        toggleSections(state) {
            state.sections.forEach((sec, index) => {
                if (index !== 0) {
                    sec.questions.forEach((q) => {
                        q["_changed"] = true;
                    });
                    state.sections[0].questions.push(...sec.questions);
                    sec.questions = [];
                }
            });
            state.faq.withSections = !state.faq.withSections;
        },
        addQuestion(state, question) {
            const section = state.sections.find((x) => x.id === question.sectionId);
            if (section) {
                question["_created"] = true;
                question.id = randomId();
                question.order = section.questions.length;
                question.hide = false;
                section.questions.push(question);
            }
        },
        addSection(state, name) {
            state.sections.push({
                _created: true,
                id: randomId(),
                faqId: state.faq.id,
                name: name,
                questions: [],
            });
        },
        removeSection(state, section) {
            if (section._created) {
                const index = state.sections.indexOf(section);
                if (index > -1) {
                    state.sections.splice(index, 1);
                }
            } else {
                delete section._changed;
                section["_deleted"] = true;
                section.questions.forEach((question, index) => {
                    if (question._created) {
                        section.questions.splice(index, 1);
                    } else {
                        delete question._changed;
                        question["_deleted"] = true;
                    }
                });
            }
        },
    },
    actions: {
        refreshIframe({ state, getters }) {
            const faq = cloneDeep(state.faq);
            faq.sections = cloneDeep(state.sections);
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && faq.id) {
                state.iframe.contentWindow.postMessage({ faqData: faq }, getters.appUrl);
            }
        },
        removeQuestion({ commit }, payload) {
            commit("removeQuestion", payload);
            commit("setQuestions");
        },
        cloneQuestion({ commit }, question) {
            const clonedQuestion = cloneDeep(question);
            commit("addQuestion", clonedQuestion);
        },
        showSchema({ state, commit }) {
            let schemaQuestions = "";
            const allQuestions = [];
            state.sections.forEach((section) => {
                allQuestions.push(...section.questions);
            });
            allQuestions.forEach((question) => {
                schemaQuestions += `{
                    "@type": "Question",
                    "name": ${JSON.stringify(question.question)},
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": ${JSON.stringify(question.answer)}
                    }
                },`;
            });

            const schema = `<script type="application/ld+json">
                {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [${schemaQuestions.replace(/,$/, "")}]
                }
                </script>`;

            const modalData = {
                type: "code",
                title: t("faq.schema_code"),
                code: schema,
                actionBtn: t("shared.copy_code"),
                callback: () => {
                    copy(schema);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("shared.code_copied"),
                        },
                        { root: true }
                    );
                },
            };

            commit("showModal", modalData, { root: true });
        },
        showNoQuestionsModal({ commit }) {
            commit(
                "showModal",
                {
                    type: "info",
                    title: t("faq.empty.title"),
                    desc: t("faq.empty.desc"),
                    cancelBtn: t("shared.cancel"),
                    actionBtn: t("faq.empty.cta"),
                    callback: () => {
                        EventBus.$emit("openSidebar", "data");
                        setTimeout(() => EventBus.$emit("createQuestion"), 100);
                    },
                },
                { root: true }
            );
        },
        publishProject({ getters, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }
            if (!getters.nQuestions) {
                dispatch("faq/showNoQuestionsModal", null, { root: true });
                return;
            }
            dispatch("hostCode");
        },
        async unpublishFaq({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.faq.uuid });
            commit("setUnpublished", state.faq, { root: true });
            dispatch("faq/projects/mergeSnapshotChanges", null, { root: true });
        },
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        async unpublish({ _ }, { uuid }) {
            return projectsService.publish({ tool: "faq", uuid, publish: false });
        },
        showLinkModal({ state, getters, commit, rootState }) {
            const modalData = {
                type: "code",
                title: t("faq.share_modal.title"),
                code: getters.faqIframe,
                actionBtn: t("shared.copy_embed"),
                cancelBtn: t("faq.share_modal.copy_link"),
                callback: () => {
                    copy(getters.faqIframe);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("shared.code_copied"),
                        },
                        { root: true }
                    );
                },
                cancelCallback: () => {
                    copy(`${rootState.globalUrl}/faqs/${state.faq.uuid}`);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("shared.link_copied"),
                        },
                        { root: true }
                    );
                },
            };
            commit("showModal", modalData, { root: true });
        },
        async hostCode({ state, rootState, commit, rootGetters, dispatch }) {
            rootState.waiting = true;
            const firstPublish = !rootGetters.isPublished(state.faq);

            await nextTick();

            try {
                await projectsService.publish({ tool: "faq", uuid: state.faq.uuid, publish: true });

                commit("setPublished", state.faq, { root: true });
                dispatch("faq/projects/mergeSnapshotChanges", null, { root: true });
                if (firstPublish) {
                    dispatch("showLinkModal");
                }
                rootState.waiting = false;
            } catch {
                rootState.waiting = false;
            }
        },
        changeFontFamily({ state }, payload) {
            state.faq.fontFamily = payload;
        },
        async exampleImport2({ state, dispatch, rootState }) {
            state.faqs = (await import(`@/apps/faq/database/faqs_${rootState.lang}.json`)).default;
            const questions = state.faqs
                .map((q, index) => {
                    const sectionName = `;${t("faq.section")}${index > 1 ? 2 : 1}`;
                    return q.question + ";" + q.answer + sectionName;
                })
                .join("\n");
            dispatch(
                "download",
                {
                    data: "\ufeff" + questions,
                    filename: "faq.csv",
                    type: "text/csv",
                },
                { root: true }
            );
        },
        async fillWithExample({ state, commit }, payload) {
            const questions = payload || [];
            questions.forEach((question) => {
                question.sectionId = state.sections[0]?.id;
                commit("addQuestion", question);
            });
            commit(
                "showAlert",
                {
                    type: "success",
                    title: t("faq.import.sample_success"),
                },
                { root: true }
            );
        },
        importQuestions2({ state, commit }, payload) {
            const content = payload.replaceAll("\r", "").split("\n");

            const sections = [];
            content.forEach((question) => {
                if (question.length) {
                    const questionSplit = question.split(";");
                    sections.push(
                        questionSplit[2] ? questionSplit[2] : t("faq.import.sample_section")
                    );
                }
            });
            const uniqueSections = [...new Set(sections)];
            uniqueSections.forEach((section) => {
                const questions = [];
                content.forEach((question) => {
                    if (question.length > 1) {
                        const questionSplit = question.split(";");
                        const sectionName = questionSplit[2]
                            ? questionSplit[2]
                            : t("faq.import.sample_section");
                        if (sectionName === section)
                            questions.push({
                                question: questionSplit[0],
                                answer: questionSplit[1],
                            });
                    }
                });
                const existingSection = state.sections.find((sec) => sec.name === section);

                if (!existingSection) {
                    commit("addSection", section);
                }

                questions.forEach((question) => {
                    question.sectionId = existingSection
                        ? existingSection.id
                        : state.sections[state.sections.length - 1].id;
                    commit("addQuestion", question);
                });
            });

            commit(
                "showAlert",
                {
                    type: "success",
                    title: t("faq.import.success"),
                },
                { root: true }
            );
        },
        exportQuestions({ state, commit, dispatch }) {
            let questions = "";
            const sections = state.faq.withSections ? state.sections : [state.sections[0]];

            sections.forEach((section) => {
                questions += section.questions
                    .map((q) => {
                        const sectionName = state.faq.withSections ? `;${section.name}` : "";
                        return q.question + ";" + q.answer + sectionName;
                    })
                    .join("\n");
                questions += "\n";
            });

            dispatch(
                "download",
                {
                    data: "\ufeff" + questions,
                    filename: "faq.csv",
                    type: "text/csv",
                },
                { root: true }
            );
            commit(
                "showAlert",
                {
                    type: "success",
                    title: t("faq.import.export_success"),
                },
                { root: true }
            );
        },
    },
};
